// components/promotions/PromotionForm.jsx
// Create/edit dialog for a single promotion with per-item pricing grid.
//
// All form logic (state, member loading, item search, promo prices and their
// checks, save + blast-radius confirm + overlap-conflict parsing) lives in the
// usePromotionDraft hook; the price arithmetic itself in ./promoPrice.js. This component is the *view*: MUI dialog + AG Grid
// rendering over that hook. Keep it presentation-only; behaviour changes go in
// the hook.

import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Select, MenuItem,
  FormControl, InputLabel, ToggleButton, ToggleButtonGroup,
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Alert, Box, InputAdornment,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ICON, CaretDown, Trash, X } from '../../icons';
import InfoHint from '../common/InfoHint';
import AlertActionSlot from '../common/AlertActionSlot';
import { useBrowserDismissal } from '../../utils/noticeDismissal';
import ConfirmDialog from '../common/ConfirmDialog';
import DialogErrorBanner from '../common/DialogErrorBanner';
import AppDateRangePicker from '../common/AppDateRangePicker';
import '../../styles/grid-page.css';
import { useSuppliers } from '../../hooks/useReferenceData';
import { usePromotionDraft } from '../../hooks/usePromotionDraft';
import { useAgGridTheme } from '../../utils/agGridTheme';
import GridIconAction from '../../components/grid/GridIconAction';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, iconActionColumn, flexColumn } from '../../utils/gridDefaults';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useGridState } from '../../hooks/useGridState';
import ProductCategoryPicker from '../common/ProductCategoryPicker';
import TypeaheadSearchField from '../common/TypeaheadSearchField';
import { ItemOptionContent } from '../common/ItemOption';
import { useIsShopify, productColumn } from '../grid/productIdentity';
import { useItemSearchCopy } from '../../features/item-filter';
import { priceSyncFailureReason } from './priceSyncReason';

function PromotionForm({ open, onClose, onSaved, editPromotion = null }) {
  const { t } = useTranslation();
  const columnTypes = useColumnTypes();
  const { formatNumber } = useFormatters();
  const isShopify = useIsShopify();
  const itemSearch = useItemSearchCopy();
  const gridTheme = useAgGridTheme();
  const gridRef = useRef(null);
  // Stable identity — see ForecastGrid's getRowId for the AG Grid rationale.
  const getRowId = useCallback((params) => String(params.data.item_id), []);
  const gridState = useGridState('promotion_form_items');
  const { data: suppliers, loading: suppliersLoading, error: suppliersError } = useSuppliers();


  const { state, actions, derived } = usePromotionDraft({
    open,
    editPromotion,
    onSaved,
    onClose,
  });

  const {
    name, startDate, endDate, notes,
    liftType, liftValue, preAdjustment, postAdjustment, discountPercentage,
    pricingMode, selectionMode, gridItems, selectedSupplier, selectedProductCategory,
    itemSearchText, itemSuggestions,
    saving, savingAs, error, conflicts, confirmPrice, priceSync,
    priceCheckOpen, highlightPriceIssues, confirmPercentSwitch,
  } = state;

  const {
    setName, setStartDate, setEndDate, setNotes,
    setLiftType, setLiftValue, setPreAdjustment, setPostAdjustment,
    setDiscountPercentage, setSelectedSupplier, setSelectedProductCategory,
    setItemSearchText, setConfirmPrice,
    changeSelectionMode, loadItemsForMode,
    addItem, removeItem, setPromoPrice,
    changePricingMode, switchToPercent, setConfirmPercentSwitch,
    setPriceCheckOpen, removePriceIssueItems,
    dismissError, save,
  } = actions;

  const {
    isValid, missingRequirements, conflictingItemCodes, priceRows, priceIssueCounts, currencyDigits,
  } = derived;
  // A non-empty value the hook could not parse: the only way a numeric token
  // lands in missingRequirements while the box shows text.
  const numberError = (token, raw) =>
    raw !== '' && missingRequirements.includes(token) ? t('promotions.enterNumber') : undefined;
  const liftError = numberError('liftValue', liftValue);
  const discountError = numberError('discountPercentage', discountPercentage)
    ?? (missingRequirements.includes('discountRange')
      ? t('promotions.discountRange', { min: 1, max: 99 })
      : undefined);
  const preError = numberError('preAdjustment', preAdjustment);
  const postError = numberError('postAdjustment', postAdjustment);

  // First-time explanation of how Shopify sync works, once per browser: it is
  // about the app, not about any one store's data.
  const [educationSeen, dismissEducation] = useBrowserDismissal('manexys.promotions.shopifyEducation.seen');
  const showEducation = !educationSeen;

  // Grid column definitions
  const columnDefs = useMemo(() => [
    ...(isShopify
      ? [productColumn({ headerName: t('promotions.grid.product'), ...flexColumn(2, 200) })]
      : [
          { field: 'item_code', headerName: t('promotions.grid.itemCode'), ...flexColumn(1, 100) },
          { field: 'item_name', headerName: t('promotions.grid.itemName'), ...flexColumn(2, 150) },
        ]),
    {
      field: 'current_sell_price',
      headerName: t('promotions.grid.currentPrice'),
      type: 'decimalColumn',
      width: 120,
      // In the currency's own decimals, like the promo price beside it.
      valueFormatter: (params) => (params.value == null ? '—' : formatNumber(params.value, currencyDigits)),
    },
    {
      field: 'promotional_sell_price',
      headerName: t('promotions.grid.promoPrice'),
      type: 'decimalColumn',
      width: 130,
      editable: pricingMode === 'individual',
      // No promo price = the item keeps its price: show that price, muted,
      // rather than a dash that reads as "no price". Decimals = the step the
      // price is rounded to.
      valueFormatter: (params) => {
        const shown = params.value ?? params.data?.current_sell_price;
        return shown == null ? '—' : formatNumber(shown, currencyDigits);
      },
      cellClassRules: {
        'promo-price-unchanged': (params) => params.data?.promotional_sell_price == null,
      },
      // The hook owns the rows (and rounds the price); the grid only reports.
      valueSetter: (params) => {
        setPromoPrice(params.data.item_id, params.newValue);
        return false;
      },
    },
    // The discount each price takes off, in both modes: the same columns
    // whichever way the prices are set.
    {
      field: 'discount_pct',
      headerName: t('promotions.grid.discount'),
      type: 'decimalColumn',
      width: 110,
      valueFormatter: (params) => (params.value == null ? '—' : `${formatNumber(params.value, 1)}%`),
    },
    iconActionColumn({
      cellRenderer: GridIconAction,
      cellRendererParams: {
        icon: Trash,
        label: t('btn.remove'),
        onClick: (row) => removeItem(row.item_id),
      },
    }),
  ], [t, removeItem, isShopify, pricingMode, formatNumber, setPromoPrice, currencyDigits]);

  const getRowClass = useCallback((params) => {
    if (conflictingItemCodes.has(params.data?.item_code)) return 'promo-row-conflict';
    if (highlightPriceIssues && params.data?.price_issue) return 'promo-row-price-check';
    return '';
  }, [conflictingItemCodes, highlightPriceIssues]);

  // The popup a save stopped by rows without a real promo price opens: one
  // line per kind of problem, then what to do about it.
  const priceIssueLines = [
    [priceIssueCounts.unchanged, t('promotions.priceCheck.unchanged', { count: priceIssueCounts.unchanged })],
    [priceIssueCounts.aboveCurrent, t('promotions.priceCheck.aboveCurrent', { count: priceIssueCounts.aboveCurrent })],
    [priceIssueCounts.noPrice, t('promotions.priceCheck.noPrice', { count: priceIssueCounts.noPrice })],
  ].filter(([count]) => count > 0).map(([, line]) => `• ${line}`);
  const onlyNoPrice = priceIssueCounts.unchanged + priceIssueCounts.aboveCurrent === 0;
  const priceCheckMessage = [
    [t('promotions.priceCheck.intro'), ...priceIssueLines].join('\n'),
    t(onlyNoPrice ? 'promotions.priceCheck.hintNoPrice' : 'promotions.priceCheck.hint'),
  ].join('\n\n');

  return (
    <>
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className="dialog-form dialog-form--wide"
    >
      <DialogTitle>
        {editPromotion ? t('promotions.editPromotion') : t('promotions.createPromotion')}
        <IconButton onClick={onClose} size="small" disabled={saving}><X size={ICON.xl} /></IconButton>
      </DialogTitle>

      <DialogContent>
        {priceSync && ['pending', 'submitting', 'running'].includes(priceSync.status) && (
          <Alert severity="info">
            {t('promotions.priceSyncInProgress')}
          </Alert>
        )}
        {priceSync?.status === 'failed' && (
          <Alert severity="warning">{t('promotions.priceSyncRefused')}</Alert>
        )}
        {priceSync?.status === 'partial' && (
          <Alert severity="warning">
            {t('promotions.priceSyncFailed', { count: priceSync.failed_count })}{' '}
            {t(priceSync.direction === 'activate'
              ? 'promotions.priceSyncSendAgain' : 'promotions.priceSyncPutBack')}
            {priceSync.failed_items.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                {/* `item_label` is the server's merchant-facing name for the item
                    (product name + SKU, api/services/item_lookup.item_label): the
                    item code on these Shopify rows is a variant id. */}
                {priceSync.failed_items.slice(0, 10)
                  .map(fi => `${fi.item_label} (${priceSyncFailureReason(fi, t)})`).join(', ')}
              </Box>
            )}
          </Alert>
        )}
        {/* Section: Basics */}
        <Typography className="dialog-form-section">{t('promotions.basics')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t('promotions.name')}
            value={name}
            onChange={e => setName(e.target.value)}
            required
            size="small"
            placeholder={t('promotions.namePlaceholder')}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <AppDateRangePicker
            startValue={startDate}
            onStartChange={setStartDate}
            endValue={endDate}
            onEndChange={setEndDate}
            startLabel={t('promotions.startDate')}
            endLabel={t('promotions.endDate')}
            startRequired
            endRequired
            endRequiresStart
          />
        </Box>
        <TextField
          label={t('promotions.notes')}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          multiline
          rows={2}
          fullWidth
          size="small"
        />

        {/* Section: Items */}
        <Typography className="dialog-form-section">{t('promotions.affectedItems')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={selectionMode}
            exclusive
            onChange={(_, v) => changeSelectionMode(v)}
            size="small"
          >
            <ToggleButton value="all">{t('promotions.allItems')}</ToggleButton>
            <ToggleButton value="individual">{t('promotions.individual')}</ToggleButton>
            <ToggleButton value="supplier">{t('promotions.bySupplier')}</ToggleButton>
            <ToggleButton value="product_category">{t('promotions.byProductCategory')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {selectionMode === 'all' && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
            {t('promotions.allItemsInfo')}
          </Typography>
        )}

        {selectionMode === 'individual' && (
          <TypeaheadSearchField
            label={itemSearch.label}
            placeholder={itemSearch.placeholder}
            value={itemSearchText}
            onChange={setItemSearchText}
            suggestions={itemSuggestions.suggestions}
            loading={itemSuggestions.loading}
            failed={itemSuggestions.failed}
            getOptionKey={(item) => item.item_id}
            renderOption={(item) => <ItemOptionContent option={item} isShopify={isShopify} />}
            onPick={addItem}
            emptyText={t('searchFilters.noItemsFound')}
          />
        )}

        {selectionMode === 'supplier' && suppliersError && (
          <Alert severity="error">{t('promotions.suppliersLoadFailed')}</Alert>
        )}

        {selectionMode === 'supplier' && !suppliersError && (
          <FormControl fullWidth size="small">
            <InputLabel>{t('promotions.selectSupplier')}</InputLabel>
            <Select
              value={selectedSupplier || ''}
              onChange={e => {
                setSelectedSupplier(e.target.value);
                loadItemsForMode('supplier', e.target.value, null);
              }}
              label={t('promotions.selectSupplier')}
              disabled={suppliersLoading}
            >
              {suppliers.map(s => (
                <MenuItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectionMode === 'product_category' && (
          <ProductCategoryPicker
            scopeToStore
            value={selectedProductCategory}
            onChange={(cat) => {
              setSelectedProductCategory(cat);
              loadItemsForMode('product_category', null, cat?.id || null);
            }}
            label={t('promotions.selectProductCategory')}
            size="small"
          />
        )}

        {/* Section: Demand Adjustment */}
        <Typography className="dialog-form-section">
          {t('promotions.demandAdjustment')}
          <InfoHint text={t('promotions.demandAdjustmentTooltip')} />
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={liftType}
            exclusive
            onChange={(_, v) => v && setLiftType(v)}
            size="small"
          >
            <ToggleButton value="percentage">{t('promotions.percentage')}</ToggleButton>
            <ToggleButton value="absolute">{t('promotions.absoluteUnits')}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label={liftType === 'percentage' ? t('promotions.liftPercent') : t('promotions.liftUnits')}
            value={liftValue}
            onChange={e => setLiftValue(e.target.value)}
            required
            error={Boolean(liftError)}
            helperText={liftError}
            size="small"
            sx={{ width: 180 }}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              input: {
                endAdornment: liftType === 'percentage'
                  ? <InputAdornment position="end">%</InputAdornment>
                  : <InputAdornment position="end">{t('promotions.units')}</InputAdornment>,
              },
            }}
          />
        </Box>

        {/* Section: Pricing */}
        <Typography className="dialog-form-section">
          {t('promotions.pricing')}
        </Typography>

        {isShopify && showEducation && (
          <Alert
            severity="info"
            action={<AlertActionSlot onDismiss={dismissEducation} />}
            sx={{ whiteSpace: 'pre-line' }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('promotions.shopifySyncEducationTitle')}
            </Typography>
            <Typography variant="body2">
              {t('promotions.shopifySyncEducationMessage')}
            </Typography>
          </Alert>
        )}

        {isShopify && !showEducation && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              {t('promotions.shopifySyncBanner')}
            </Typography>
          </Alert>
        )}

        {/* 'All items' has no rows to price one by one: the discount only. */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {selectionMode !== 'all' && (
            <ToggleButtonGroup
              value={pricingMode}
              exclusive
              onChange={(_, v) => changePricingMode(v)}
              size="small"
            >
              <ToggleButton value="percent">{t('promotions.pricingPercent')}</ToggleButton>
              <ToggleButton value="individual">{t('promotions.pricingIndividual')}</ToggleButton>
            </ToggleButtonGroup>
          )}
          {pricingMode === 'percent' && (
            <TextField
              label={t('promotions.discountPercent')}
              value={discountPercentage}
              onChange={e => setDiscountPercentage(e.target.value)}
              error={Boolean(discountError)}
              helperText={discountError}
              size="small"
              sx={{ width: 180 }}
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              }}
            />
          )}
        </Box>
        {pricingMode === 'percent' && (
          <Typography variant="body2" color="text.secondary">
            {t('promotions.percentNote')}
          </Typography>
        )}

        {/* Item price grid (not shown for "all items" mode) */}
        {selectionMode !== 'all' && gridItems.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('promotions.affectedItemsPreview')}
          </Typography>
        )}
        {selectionMode !== 'all' && gridItems.length > 0 && gridTheme && (
          <Box sx={{ height: 300, flexShrink: 0 }}>
            <AgGridReact
              ref={gridRef}
              {...defaultGridProps}
              {...editableGridProps}
              theme={gridTheme}
              columnDefs={columnDefs}
              columnTypes={columnTypes}
              rowData={priceRows}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              getRowClass={getRowClass}
              sideBar={false}
              getContextMenuItems={copyOnlyContextMenuItems}
              {...gridState}
            />
          </Box>
        )}

        {selectionMode !== 'all' && gridItems.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t('promotions.noItemsSelected')}
          </Typography>
        )}

        {/* Pre/Post adjustments (collapsible) */}
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'var(--radius-sm)', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<CaretDown size={ICON.md} />}>
            <Typography variant="body2">{t('promotions.prePostAdjustments')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Typography variant="body2" color="text.secondary">
              {t('promotions.prePostAdjustmentsIntro')}
            </Typography>
            <TextField
              label={t('promotions.preAdjustment')}
              value={preAdjustment}
              onChange={e => setPreAdjustment(e.target.value)}
              error={Boolean(preError)}
              helperText={preError ?? t('promotions.preAdjustmentHelp')}
              size="small"
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              }}
              fullWidth
            />
            <TextField
              label={t('promotions.postAdjustment')}
              value={postAdjustment}
              onChange={e => setPostAdjustment(e.target.value)}
              error={Boolean(postError)}
              helperText={postError ?? t('promotions.postAdjustmentHelp')}
              size="small"
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              }}
              fullWidth
            />
          </AccordionDetails>
        </Accordion>

        <DialogErrorBanner
          error={error}
          heading={conflicts.length > 0 ? t('promotions.overlap.heading') : undefined}
          onClose={dismissError}
        >
          {conflicts.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: error ? 1 : 0 }}>
              {conflicts.map(c => {
                const visible = (c.overlap_items || []).map(i => i.label).join(', ');
                const hidden = Math.max(0, (c.overlap_count || 0) - (c.overlap_items?.length || 0));
                const statusLabel = t(`promotions.status_${c.status}`);
                return (
                  <Box key={c.promotion_id} sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
                      {c.promotion_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {statusLabel} · {c.date_range}
                    </Typography>
                    <Typography variant="body2">
                      {t('promotions.overlap.conflictingItems')}{' '}
                      <Box component="span" sx={{ fontFamily: 'monospace' }}>{visible}</Box>
                      {hidden > 0 && (
                        <Box component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                          {t('promotions.overlap.moreItems', { count: hidden })}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary">
                {t('promotions.overlap.hint')}
              </Typography>
            </Box>
          )}
        </DialogErrorBanner>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving}>{t('btn.cancel')}</Button>
        <Button
          variant="outlined"
          onClick={() => save('draft')}
          disabled={!isValid || saving}
          loading={savingAs === 'draft'}
        >
          {t('promotions.saveAsDraft')}
        </Button>
        <Button
          variant="contained"
          onClick={() => save('active')}
          disabled={!isValid || saving}
          loading={savingAs === 'active'}
        >
          {t('promotions.saveAndApply')}
        </Button>
      </DialogActions>
    </Dialog>

    <ConfirmDialog
      open={!!confirmPrice}
      onCancel={() => setConfirmPrice(null)}
      onConfirm={() => {
        const target = confirmPrice.targetStatus;
        setConfirmPrice(null);
        save(target, { confirmed: true });
      }}
      loading={saving}
      title={t('promotions.confirmPriceTitle')}
      message={`${confirmPrice?.count != null
        ? t('promotions.confirmPriceBody', { count: confirmPrice.count })
        : t('promotions.confirmPriceBodyNoCount')}

${t('promotions.confirmPriceHint')}`}
      variant="warning"
      confirmLabel={t('promotions.confirmPriceConfirm')}
    />

    <ConfirmDialog
      open={priceCheckOpen}
      onCancel={() => setPriceCheckOpen(false)}
      onConfirm={removePriceIssueItems}
      title={t('promotions.priceCheck.title')}
      message={priceCheckMessage}
      variant="warning"
      confirmLabel={t('promotions.priceCheck.remove')}
      cancelLabel={t('promotions.priceCheck.back')}
    />

    <ConfirmDialog
      open={confirmPercentSwitch}
      onCancel={() => setConfirmPercentSwitch(false)}
      onConfirm={switchToPercent}
      title={t('promotions.percentSwitch.title')}
      message={t('promotions.percentSwitch.body')}
      variant="warning"
      confirmLabel={t('promotions.percentSwitch.confirm')}
    />
    </>
  );
}

export default PromotionForm;
